import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "@/shared/api/client";
import { authApi } from "@/shared/api/endpoints";
import { identify, track } from "@/shared/analytics/analytics";
import { identifyForErrors } from "@/shared/errors/errorTracking";
import type { Session, Staff, StaffRole } from "@/shared/api/types";
import { Spinner } from "@/shared/ui";

interface AuthState {
  /** The signed-in dashboard team member (a staff account, never a learner). */
  user: Staff | null;
  signInWithGoogle: (idToken: string) => Promise<void>;
  signInDev: (email: string, role: StaffRole) => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (...roles: StaffRole[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(api.current);
  // The access token lives in memory: a reload restores it from the refresh
  // cookie before any route decides the user is signed out.
  const [restored, setRestored] = useState(api.current !== null);

  useEffect(() => api.onSessionChange(setSession), []);
  useEffect(() => {
    if (restored) return;
    let live = true;
    void api.restore().finally(() => {
      if (live) setRestored(true);
    });
    return () => {
      live = false;
    };
  }, [restored]);

  // Analytics follows the session: internal id + role, never the e-mail.
  const userId = session?.staff.id;
  const role = session?.staff.role;
  useEffect(() => {
    void identify(userId && role ? { id: userId, role } : null);
    void identifyForErrors(userId ?? null);
  }, [userId, role]);

  const value = useMemo<AuthState>(
    () => ({
      user: session?.staff ?? null,
      signInWithGoogle: async (idToken) => {
        api.setSession(await authApi.google(idToken));
        void track("login", { method: "google" });
      },
      signInDev: async (email, role) => {
        api.setSession(await authApi.dev(email, role));
        void track("login", { method: "dev" });
      },
      signOut: async () => {
        api.setSession(null); // every tab
        await authApi.logout().catch(() => undefined);
      },
      hasRole: (...roles) => !!session && roles.includes(session.staff.role),
    }),
    [session],
  );

  if (!restored) return <Spinner />;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
